<template>
  <v-dialog v-model="open" persistent max-width="620px">
    <v-form ref="form" v-model="valid" lazy-validation>
      <v-card>
        <v-card-title>
          <v-icon color="primary" class="me-2">mdi-account-plus</v-icon>
          <span class="text-h6">{{ $t('add_student') }}</span>
        </v-card-title>

        <v-card-subtitle class="pt-2">{{ $t('add_student_hint') }}</v-card-subtitle>

        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="name"
                  :rules="requiredRules"
                  :label="$t('student_name') + '*'"
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="identification"
                  :rules="requiredRules"
                  :label="$t('student_id') + '*'"
                  required
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="notes"
                  :label="$t('notes')"
                  :hint="$t('student_notes_hint')"
                  persistent-hint
                ></v-text-field>
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>

            <v-row>
              <v-col cols="12" sm="6">
                <!-- an existing address attaches the student to that parent, a
                     new one creates them -->
                <v-combobox
                  v-model="parentEmail"
                  :items="parentEmails"
                  :rules="emailRules"
                  :label="$t('parent_email') + '*'"
                  :hint="$t('existing_or_new_parent')"
                  persistent-hint
                  required
                ></v-combobox>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="parentName"
                  :rules="isNewParent ? requiredRules : []"
                  :label="$t('parent_name') + (isNewParent ? '*' : '')"
                  :disabled="!isNewParent"
                  :hint="isNewParent ? $t('new_parent_will_be_created') : $t('existing_parent_found')"
                  persistent-hint
                ></v-text-field>
              </v-col>
            </v-row>
            <!-- only a parent being created here needs one; an existing parent
                 already has a password of their own -->
            <v-row v-if="isNewParent">
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="parentPassword"
                  :rules="isNewParent ? passwordRules : []"
                  :label="$t('Password') + '*'"
                  :hint="$t('parent_password_hint')"
                  persistent-hint
                  :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                  :type="showPassword ? 'text' : 'password'"
                  @click:append="showPassword = !showPassword"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="isSaving" @click="open = false">{{ $t('close') }}</v-btn>
          <v-btn text :loading="isSaving" @click="submit(true)">
            {{ $t('save_and_add_another') }}
          </v-btn>
          <v-btn color="primary" text :loading="isSaving" @click="submit(false)">
            {{ $t('save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
/*
 * Adds one student, to an existing parent or a new one.
 *
 * Before this the only way for a school to add a student was to build a
 * spreadsheet and upload it, which is a strange thing to do for a single child
 * joining mid-term.
 */
export default {
  name: "AddStudentDialog",
  props: {
    value: { type: Boolean, default: false },
    // existing parents of this school, so the admin can pick rather than type
    parents: { type: Array, default: () => [] },
  },
  data() {
    return {
      valid: true,
      isSaving: false,
      name: "",
      identification: "",
      notes: "",
      parentEmail: "",
      parentName: "",
      parentPassword: "",
      showPassword: false,
      requiredRules: [(v) => !!v || this.$t("required_field")],
      emailRules: [
        (v) => !!v || this.$t("E-mail is required"),
        (v) => /.+@.+\..+/.test(v) || this.$t("E-mail must be valid"),
      ],
      passwordRules: [
        (v) => !!v || this.$t("password_required"),
        (v) => (v && v.length >= 6) || this.$t("password_min_length"),
      ],
    };
  },
  computed: {
    open: {
      get() { return this.value; },
      set(val) { this.$emit("input", val); },
    },
    parentEmails() {
      return this.parents.map((parent) => parent.email).filter(Boolean);
    },
    matchedParent() {
      const email = (this.parentEmail || "").trim().toLowerCase();
      if (!email) return null;
      return this.parents.find((p) => (p.email || "").toLowerCase() === email) || null;
    },
    isNewParent() {
      return !!(this.parentEmail || "").trim() && !this.matchedParent;
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) this.reset();
    },
    matchedParent(parent) {
      //show whose child this will become
      this.parentName = parent ? parent.name : "";
    },
  },
  methods: {
    reset() {
      this.name = "";
      this.identification = "";
      this.notes = "";
      this.parentEmail = "";
      this.parentName = "";
      this.parentPassword = "";
      this.showPassword = false;
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    submit(addAnother) {
      if (!this.$refs.form.validate()) return;

      this.isSaving = true;
      axios
        .post("/school-students/add", {
          name: this.name,
          student_identification: this.identification,
          notes: this.notes,
          parent_email: (this.parentEmail || "").trim(),
          parent_name: this.parentName,
          //the password travels with the request, so a new parent is created
          //with it in one go. It used to be patched afterwards by a second
          //request, and when that failed the account kept a default password
          //that the admin had never been told about
          parent_password: this.isNewParent ? this.parentPassword : null,
        })
        .then((response) => {
          this.$notifySuccess(this.$t("student_added", { name: this.name }));

          this.$emit("added");

          if (addAnother) {
            //keep the parent, clear the child: siblings are the common case
            this.name = "";
            this.identification = "";
            this.notes = "";
            if (this.$refs.form) this.$refs.form.resetValidation();
          } else {
            this.open = false;
          }
        })
        .catch((error) => {
          this.$notifyError(error, this.$t("error_adding_student"));
        })
        .then(() => {
          this.isSaving = false;
        });
    },
  },
};
</script>
